import React, { useState, useEffect } from "react";
import { Box, Typography, Avatar, Stack, Container, CircularProgress } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import houseImage from "../../assets/newprojects.webp";
import useResponsive from "../../hooks/useResponsive";
import { useMediaQuery } from "@mui/material";
import { getallproperties } from "../../services/apiService";
import { useNavigate } from "react-router-dom";

const nodePositions = [
    { left: 210, top: 110 },
    { left: 295, top: 265 },
    { left: 235, top: 435 },
];

const NewProjects = () => {
    const [selected, setSelected] = useState(0);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { isSmallScreen } = useResponsive();
    const navigate = useNavigate();
    
    // Tablet/iPad Mini: 750-1050px
    const isTablet = useMediaQuery('(min-width:700px) and (max-width:1100px)');

    // Fetch properties on component mount
    useEffect(() => {
        const fetchProperties = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await getallproperties();
                
                if (response && response.properties) {
                    // Transform the API response to match our component structure
                    const transformedProjects = response.properties.map(property => ({
                        name: property.name || property.propertyName || property.title || "Property",
                        location: property.locations && property.locations.length > 0 
                            ? `${property.locations[0].city || ''}, ${property.locations[0].state || ''}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '') || property.locations[0].address
                            : property.location || property.address || "Location not specified",
                        image: property.proertyImage || 
                               (property.galleries && property.galleries.length > 0 ? property.galleries[0] : null) ||
                               "https://via.placeholder.com/400x300?text=No+Image",
                        id: property._id || property.id
                    }));
                    
                    setProjects(transformedProjects);
                } else {
                    setProjects([]);
                }
            } catch (err) {
                console.error('Error fetching properties:', err);
                setError('Failed to load properties. Please try again later.');
                setProjects([]);
            } finally {
                setLoading(false);
            }
        };

        fetchProperties();
    }, []);

    // Auto-change project every 5 seconds (only on desktop and when we have projects)
    useEffect(() => {
        if (!isSmallScreen && projects.length > 1) {
            const interval = setInterval(() => {
                setSelected(prev => (prev + 1) % projects.length);
            }, 2000);
            return () => clearInterval(interval);
        }
    }, [isSmallScreen, projects.length]);

    // Show loading state
    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 400,
                        backgroundColor: "#fff",
                    }}
                >
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    // Show error state
    if (error) {
        return (
            <Container maxWidth="xl">
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 400,
                        backgroundColor: "#fff",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Typography color="error" variant="h6">
                        {error}
                    </Typography>
                </Box>
            </Container>
        );
    }

    // Show empty state
    if (projects.length === 0) {
        return (
            <Container maxWidth="xl">
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 400,
                        backgroundColor: "#fff",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Typography variant="h6" color="text.secondary">
                        No properties available at the moment.
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
        <Box
            sx={{
                display: isTablet ? "block" : "flex",
                flexDirection: isSmallScreen ? "column" : isTablet ? "row" : "row",
                minHeight: isTablet ? 600 : 430,
                justifyContent: isTablet ? "center" : "space-between",
                alignItems: isTablet ? "stretch" : "center",
                position: "relative",
                px: isSmallScreen ? 0 : isTablet ? 2 : 4,
                py: isTablet ? 2 : 1,
                backgroundColor: "#fff",
                gap: isSmallScreen ? 4 : isTablet ? 2 : 0,
            }}
        >
            {/* Top on mobile / Left on desktop */}
            <Box sx={{
                width: isSmallScreen ? "100%" : isTablet ? "100%" : "30%",
                display: "flex", justifyContent: "center",  mb: isTablet ? 3 : 0
            }}>
                <Box
                    sx={{
                        width:isSmallScreen ? "100%" : isTablet ? 350 : 450,
                        maxWidth: "100%",
                        height: isTablet ? 300 : 380,
                        borderRadius: 4,
                        overflow: "hidden",
                        boxShadow: 3,
                        bgcolor: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.3s",
                    }}
                >
                    <img src={projects[selected]?.image} alt="Building" loading="lazy"
                        style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 16, transition: "all 0.3s",  }}
                    />
                </Box>
            </Box>

            {/* Center Curve + House Image (only for desktop) */}
            {!isSmallScreen && !isTablet && (
                <Box
                    sx={{
                        width: 320,
                        height: 520,
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <svg
                        width="250"
                        height="620"
                        style={{
                            position: "absolute",
                            left: "80%",
                            top: 0,
                            transform: "translateX(-50%)",
                            zIndex: 1,
                        }}
                    >
                        <path
                            d="M80 110 Q240 250 100 450"
                            stroke="#d32f2f"
                            strokeWidth="5"
                            fill="none"
                        />
                    </svg>

                    {nodePositions.map((node, idx) => (
                        <Box
                            key={idx}
                            onClick={() => setSelected(idx)}
                            sx={{
                                position: "absolute",
                                left: node.left,
                                top: node.top,
                                transform: "translate(-50%, -50%)",
                                width: 40,
                                height: 40,
                                bgcolor: selected === idx ? "#d32f2f" : "#f5f5f5",
                                border: selected === idx ? "2px solid #d32f2f" : "4px solid #e0e0e0",
                                borderRadius: "50%",
                                zIndex: 2,
                                boxShadow: selected === idx ? "0 2px 8px 0 rgba(211,47,47,0.15)" : "none",
                                cursor: "pointer",
                                transition: "all 0.2s",
                            }}
                        />
                    ))}

                    <Box
                        sx={{
                            position: "absolute",
                            top: "55%",
                            left: "45%",
                            transform: "translate(-50%, -50%)",
                            textAlign: "center",
                            zIndex: 3,
                            '@keyframes float': {
                                '0%, 100%': {
                                    transform: 'translate(-50%, -50%) translateY(0)',
                                },
                                '50%': {
                                    transform: 'translate(-50%, -50%) translateY(-16px)',
                                },
                            },
                            animation: 'float 3s ease-in-out infinite',
                        }}
                    >
                        <img 
                            loading="lazy" 
                            src={houseImage} 
                            alt="House" 
                            height={230} 
                            width={230} 
                            style={{
                                display: 'block',
                               
                            }}
                        />
                    </Box>
                </Box>
            )}

            {/* Bottom on mobile / Right on desktop */}
            <Box sx={{
                width: isSmallScreen ? "100%" : isTablet ? "100%" : "33%",
                mt: isTablet ? 3 : 0
            }}>
                <Stack spacing={isTablet ? 2 : 3}>
                    {projects.slice(0, 3).map((project, index) => (
                        <Box
                            key={project.id || index}
                            display="flex"
                            alignItems="center"
                            sx={{
                                backgroundColor: "#fff",
                                p: 1.5,
                                borderRadius: 2,
                                boxShadow: "0 2px 8px 0 rgba(60,72,88,.08)",
                                minHeight: isTablet ? 60 : 70,
                                position: "relative",
                                cursor: "pointer",
                                border: selected === index ? "2px solid #d32f2f" : "2px solid transparent",
                                transition: "border 0.2s",
                                width: isTablet ? "98%" : "93%",
                            }}
                            onClick={() => {
                                try {
                                  // Store the property ID in sessionStorage
                                  sessionStorage.setItem('propertyId', project.id);
                                  
                                  // Open property details page in new tab without ID in URL
                                  window.open('/propertydetails', '_blank');
                                } catch (error) {
                                  // Fallback: try direct navigation
                                  window.open('/propertydetails', '_blank');
                                }
                              }}
                        >
                            <Avatar
                                src={project.image}
                                variant="rounded"
                                sx={{
                                    width: isTablet ? 48 : 60,
                                    height: isTablet ? 48 : 60,
                                    mr: 2,
                                    borderRadius: 2,
                                    boxShadow: 1,
                                    objectFit: "cover",
                                }}
                            />
                            <Box>
                                <Typography fontWeight={600} fontSize={isTablet ? "0.95rem" : "1rem"} color="#222">
                                    {project.name}
                                </Typography>
                                <Box display="flex" alignItems="center" mt={0.5}>
                                    <LocationOnIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                                    <Typography variant="body2" ml={0.5} color="#888">
                                        {project.location}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Stack>
            </Box>
        </Box>
        </Container>
    );
};

export default NewProjects;
